import { useState, useEffect, useRef } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Link } from 'react-router-dom';

// Mock data types
interface Creator {
  id: string;
  username: string;
  displayName: string;
  avatar: string;
  verified: boolean;
  type: 'creator';
}

interface Page {
  id: string;
  title: string;
  avatar: string;
  verified: boolean;
  type: 'page';
}

interface Music {
  id: string;
  title: string;
  artist: string;
  artist_avatar: string;
  verified: boolean;
  type: 'music';
}

type SearchResult = Creator | Page | Music;

// Mock search results
const mockSearchResults: SearchResult[] = [
  {
    id: '1',
    username: 'johndoe',
    displayName: 'John Doe',
    avatar: '/images/avatars/avatar-1.jpg',
    verified: true,
    type: 'creator'
  },
  {
    id: '2',
    username: 'janedoe',
    displayName: 'Jane Doe',
    avatar: '/images/avatars/avatar-2.jpg',
    verified: false,
    type: 'creator'
  },
  {
    id: '3',
    title: 'Music Studio Page',
    avatar: '/images/pages/music-studio.jpg',
    verified: true,
    type: 'page'
  },
  {
    id: '4',
    title: 'Art Gallery',
    avatar: '/images/pages/art-gallery.jpg',
    verified: false,
    type: 'page'
  },
  {
    id: '5',
    title: 'Summer Vibes',
    artist: 'DJ Cool',
    artist_avatar: '/images/avatars/dj-cool.jpg',
    verified: true,
    type: 'music'
  },
  {
    id: '6',
    title: 'Midnight Dreams',
    artist: 'Luna Sky',
    artist_avatar: '/images/avatars/luna-sky.jpg',
    verified: false,
    type: 'music'
  },
  {
    id: '7',
    username: 'searchuser',
    displayName: 'Search User',
    avatar: '/images/avatars/avatar-3.jpg',
    verified: true,
    type: 'creator'
  },
  {
    id: '8',
    title: 'Search Engine Page',
    avatar: '/images/pages/search-page.jpg',
    verified: true,
    type: 'page'
  },
  {
    id: '9',
    title: 'Searching For You',
    artist: 'Search Artist',
    artist_avatar: '/images/avatars/search-artist.jpg',
    verified: true,
    type: 'music'
  },
  {
    id: '10',
    username: 'hashtag_lover',
    displayName: '#HashtagLover',
    avatar: '/images/avatars/avatar-4.jpg',
    verified: true,
    type: 'creator'
  },
  {
    id: '11',
    title: '#TrendingNow',
    avatar: '/images/pages/trending-page.jpg',
    verified: true,
    type: 'page'
  },
  {
    id: '12',
    title: '#1 Hit Wonder',
    artist: 'Number One',
    artist_avatar: '/images/avatars/number-one.jpg',
    verified: true,
    type: 'music'
  },
  {
    id: '13',
    username: 'emoji_fan',
    displayName: '😎 Cool Guy',
    avatar: '/images/avatars/avatar-5.jpg',
    verified: false,
    type: 'creator'
  },
  {
    id: '14',
    title: 'Emoji World 🌍',
    avatar: '/images/pages/emoji-world.jpg',
    verified: false,
    type: 'page'
  },
  {
    id: '15',
    title: 'Happy Vibes 😊',
    artist: 'DJ Emoji 🎧',
    artist_avatar: '/images/avatars/dj-emoji.jpg',
    verified: true,
    type: 'music'
  },
  {
    id: '16',
    username: 'number123',
    displayName: '123 Numbers',
    avatar: '/images/avatars/avatar-6.jpg',
    verified: true,
    type: 'creator'
  }
];

// Verified badge SVG component
const VerifiedBadge = () => (
  <svg className="w-3.5 h-3.5 ml-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
  </svg>
);

export const SearchBar = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);

  // Helper function to normalize text for searching
  const normalizeForSearch = (text: string): string => {
    return text.toLowerCase();
  };

  // Filter results based on searchQuery (only updated when Enter is pressed)
  useEffect(() => {
    if (searchQuery.length >= 3) {
      let filteredResults: SearchResult[] = [];

      // Normalize the search query
      const normalizedQuery = normalizeForSearch(searchQuery);

      // Special case for when user types 'search'
      if (normalizedQuery.includes('search')) {
        // Prioritize items with 'search' in their name/title
        filteredResults = mockSearchResults.filter(item => {
          if (item.type === 'creator') {
            return normalizeForSearch(item.displayName).includes('search') ||
              normalizeForSearch(item.username).includes('search');
          } else if (item.type === 'page') {
            return normalizeForSearch(item.title).includes('search');
          } else if (item.type === 'music') {
            return normalizeForSearch(item.title).includes('search') ||
              normalizeForSearch(item.artist).includes('search');
          }
          return false;
        });
      } else {
        // Regular search for other queries (including hashtags, numbers, emojis)
        filteredResults = mockSearchResults.filter(item => {
          if (item.type === 'creator') {
            return normalizeForSearch(item.displayName).includes(normalizedQuery) ||
              normalizeForSearch(item.username).includes(normalizedQuery);
          } else if (item.type === 'page') {
            return normalizeForSearch(item.title).includes(normalizedQuery);
          } else if (item.type === 'music') {
            return normalizeForSearch(item.title).includes(normalizedQuery) ||
              normalizeForSearch(item.artist).includes(normalizedQuery);
          }
          return false;
        });
      }

      setResults(filteredResults);
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  }, [searchQuery]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setQuery('');
      setSearchQuery('');
      setShowResults(false);
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      {isSearchOpen ? (
        <div className="absolute inset-x-0 top-0 h-16 bg-white dark:bg-black flex items-center px-4 z-50">
          <Search className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.length >= 3) {
                setSearchQuery(query);
              }
            }}
            placeholder="Search... (press Enter to search)"
            className="flex-1 px-4 py-2 bg-transparent focus:outline-none"
            autoFocus
          />
          <button
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full mr-2"
            onClick={() => console.log('Filter clicked')}
          >
            <SlidersHorizontal className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
          <button
            onClick={handleSearchToggle}
            className="ml-2 p-2 text-gray-600 dark:text-gray-300 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Search Results Dropdown */}
          {showResults && results.length > 0 && (
            <div className="absolute top-16 left-0 right-0 bg-white dark:bg-gray-900 shadow-lg rounded-b-lg max-h-96 overflow-y-auto z-50">
              {results.map((result) => (
                <Link
                  key={result.id}
                  to={`/${result.type}/${result.id}`}
                  className="flex items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  {/* Different avatar styles based on type */}
                  {result.type === 'creator' && (
                    <Avatar className="h-10 w-10 rounded-lg border border-gray-200 dark:border-gray-700">
                      <AvatarImage src={(result as Creator).avatar} alt={(result as Creator).displayName} />
                      <AvatarFallback className="rounded-lg bg-indigo-800 text-white">
                        {(result as Creator).displayName.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  {result.type === 'page' && (
                    <Avatar className="h-10 w-10 rounded-full border border-gray-200 dark:border-gray-700">
                      <AvatarImage src={(result as Page).avatar} alt={(result as Page).title} />
                      <AvatarFallback className="rounded-full bg-purple-800 text-white">
                        {(result as Page).title.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  {result.type === 'music' && (
                    <Avatar className="h-10 w-10 rounded-full border border-gray-200 dark:border-gray-700">
                      <AvatarImage src={(result as Music).artist_avatar} alt={(result as Music).artist} />
                      <AvatarFallback className="rounded-full bg-blue-800 text-white">
                        {(result as Music).artist.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  <div className="ml-3 flex-1">
                    <div className="flex items-center">
                      {result.type === 'creator' && (
                        <>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {(result as Creator).displayName}
                          </span>
                          {(result as Creator).verified && <VerifiedBadge />}
                        </>
                      )}

                      {result.type === 'page' && (
                        <>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {(result as Page).title}
                          </span>
                          {(result as Page).verified && <VerifiedBadge />}
                        </>
                      )}

                      {result.type === 'music' && (
                        <>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {(result as Music).title}
                          </span>
                          {(result as Music).verified && <VerifiedBadge />}
                        </>
                      )}
                    </div>

                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {result.type === 'creator' && `@${(result as Creator).username}`}
                      {result.type === 'page' && 'Page'}
                      {result.type === 'music' && `by ${(result as Music).artist}`}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={handleSearchToggle}
          className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <Search className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default SearchBar;
